import { STATUS_CONFIG } from '../exportPcfConstants';
import type { ExportStatus, PipingSystem } from '../types';
import type { ExportColumn } from '../hooks/useExportColumns';
import type { TableColumnFilter } from '../../../shared/ui/table/hooks/useTableFilters';
import {
  formatPipingSystemComponentColumnValue,
  getComponentColumnParameterName,
} from './componentColumns';

const caseInsensitiveKeyCache = new WeakMap<Record<string, unknown>, Map<string, string>>();

function getValueCaseInsensitive(source: Record<string, unknown>, key: string): unknown {
  let keyIndex = caseInsensitiveKeyCache.get(source);
  if (!keyIndex) {
    const nextIndex = new Map<string, string>();
    Object.keys(source).forEach((candidate) => {
      const normalized = candidate.toLowerCase();
      if (!nextIndex.has(normalized)) {
        nextIndex.set(normalized, candidate);
      }
    });
    keyIndex = nextIndex;
    caseInsensitiveKeyCache.set(source, keyIndex);
  }

  const matched = keyIndex.get(key.toLowerCase());
  return matched ? source[matched] : undefined;
}

export function getSystemParameterValue(system: PipingSystem, ...candidateKeys: Array<string | undefined>): unknown {
  const sources: Array<Record<string, unknown>> = [system];
  const props = (system as Record<string, unknown>).__props;
  if (props && typeof props === 'object') {
    sources.push(props as Record<string, unknown>);
  }

  const legacyProps = (system as Record<string, unknown>).props;
  if (legacyProps && typeof legacyProps === 'object') {
    sources.push(legacyProps as Record<string, unknown>);
  }

  for (const source of sources) {
    for (const key of candidateKeys) {
      if (!key) {
        continue;
      }

      const directValue = source[key];
      if (directValue !== undefined && directValue !== null && String(directValue).trim() !== '') {
        return directValue;
      }

      const caseInsensitiveValue = getValueCaseInsensitive(source, key);
      if (
        caseInsensitiveValue !== undefined &&
        caseInsensitiveValue !== null &&
        String(caseInsensitiveValue).trim() !== ''
      ) {
        return caseInsensitiveValue;
      }
    }
  }

  return '';
}

export function resolveDynamicColumnValue(system: PipingSystem, column: ExportColumn): unknown {
  const componentParameterName = getComponentColumnParameterName(column);
  if (componentParameterName) {
    return formatPipingSystemComponentColumnValue(system, componentParameterName);
  }

  return getSystemParameterValue(system, column.label, column.revitParameter, column.id);
}

export function normalizeSortableValue(value: unknown): number | string {
  if (typeof value === 'number') {
    return value;
  }

  return String(value ?? '').trim();
}

export function matchesCondition(value: string, filter: TableColumnFilter): boolean {
  const normalizedValue = value.trim().toLowerCase();
  const normalizedFilterValue = filter.value.trim().toLowerCase();

  switch (filter.operator) {
    case 'contains':
      return normalizedValue.includes(normalizedFilterValue);
    case 'notContains':
      return !normalizedValue.includes(normalizedFilterValue);
    case 'equals':
      return normalizedValue === normalizedFilterValue;
    case 'notEquals':
      return normalizedValue !== normalizedFilterValue;
    case 'startsWith':
      return normalizedValue.startsWith(normalizedFilterValue);
    case 'endsWith':
      return normalizedValue.endsWith(normalizedFilterValue);
    case 'isEmpty':
      return normalizedValue.length === 0;
    case 'isNotEmpty':
      return normalizedValue.length > 0;
    default:
      return true;
  }
}

export function isFilterActive(filter: TableColumnFilter | undefined): boolean {
  if (!filter) {
    return false;
  }

  if (filter.mode === 'values') {
    return filter.selectedValues !== null;
  }

  return filter.operator === 'isEmpty'
    || filter.operator === 'isNotEmpty'
    || filter.value.trim().length > 0;
}

export function formatStatusEvidence(system: PipingSystem, resolvedStatus: ExportStatus): string {
  const warnings = system.warningCount || 0;
  const errors = system.errorCount || 0;

  if (errors > 0 && warnings > 0) {
    return `${errors} error${errors === 1 ? '' : 's'} and ${warnings} warning${warnings === 1 ? '' : 's'}`;
  }

  if (errors > 0) {
    return `${errors} error${errors === 1 ? '' : 's'}`;
  }

  if (warnings > 0) {
    return `${warnings} warning${warnings === 1 ? '' : 's'}`;
  }

  if (resolvedStatus === 'NotValidated') {
    return 'not validated';
  }

  return STATUS_CONFIG[resolvedStatus].label.toLowerCase();
}
